import { useEffect, useState } from 'react';
import { Alert, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import type { QuestionnaireFormType } from '../../../../infrastructure/QuestionnaireClient';
import { ROUTES } from '../../../../routes/routes';
import { SurveyDetailTabs, SurveyHeader } from '../../components';
import { useSurveyDetail } from '../../hooks/useSurveyAdminData';
import {
  createNewSurveyForm,
  createSurveyDeployRequest,
  createSurveySaveRequest,
  getSurveySchema,
} from '../../utils/surveyForm';

const formTypes: QuestionnaireFormType[] = [
  'survey',
  'skin_quiz',
  'campaign_survey',
  'nps',
];

export default function SurveySettingsPage() {
  const { surveyId: surveyIdParam } = useParams();
  const navigate = useNavigate();
  const { client, survey, surveyId, isNew, isLoading, error, reload } = useSurveyDetail(surveyIdParam);
  const [form, setForm] = useState(createNewSurveyForm);
  const [message, setMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isNew) {
      setForm(createNewSurveyForm());
      return;
    }
    if (survey) {
      setForm({
        title: survey.title,
        surveyUuid: survey.surveyUuid,
        formType: survey.formType,
      });
    }
  }, [isNew, survey]);

  const handleDeploy = async () => {
    setIsSaving(true);
    setMessage(null);
    setSaveError(null);
    try {
      const schema = getSurveySchema(survey);
      if (isNew) {
        const created = await client.createQuestionnaire(createSurveySaveRequest(form, schema));
        const published = await client.publish(created.id);
        setMessage(`Survey deployed as v${published.latestVersion?.version ?? 1}`);
        navigate(ROUTES.survey.settings(published.id), { replace: true });
        return;
      }

      if (!surveyId) return;
      const deployed = await client.deployVersion(
        surveyId,
        createSurveyDeployRequest(
          {
            title: form.title,
            formType: form.formType,
          },
          schema,
        ),
      );
      setMessage(`Survey deployed as v${deployed.latestVersion?.version ?? '-'}`);
      await reload();
    } catch (deployError) {
      setSaveError(deployError instanceof Error ? deployError.message : 'Failed to deploy survey');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container fluid className="p-4">
      <SurveyHeader survey={survey} title={isNew ? 'New Survey' : undefined} />
      {!isNew && surveyId && <SurveyDetailTabs surveyId={surveyId} />}

      {error && <Alert variant="danger">{error}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}
      {saveError && <Alert variant="danger">{saveError}</Alert>}

      <section className="border rounded p-3 bg-white">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h2 className="h5 mb-0">Settings</h2>
          <button
            className="btn btn-primary btn-sm"
            type="button"
            onClick={() => void handleDeploy()}
            disabled={isSaving || isLoading || !form.title.trim()}
          >
            {isSaving ? <Spinner animation="border" size="sm" className="me-1" /> : <Save size={15} className="me-1" />}
            Save & Deploy
          </button>
        </div>

        <Row className="g-3">
          <Col md={6}>
            <Form.Label>Survey Title</Form.Label>
            <Form.Control value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
          </Col>
          <Col md={3}>
            <Form.Label>Survey UUID</Form.Label>
            <Form.Control value={form.surveyUuid} disabled className="font-monospace small" />
          </Col>
          <Col md={3}>
            <Form.Label>Survey Type</Form.Label>
            <Form.Select
              value={form.formType}
              onChange={(event) => setForm({ ...form, formType: event.target.value as QuestionnaireFormType })}
            >
              {formTypes.map((formType) => (
                <option key={formType} value={formType}>
                  {formType}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      </section>
    </Container>
  );
}
